import type { TFunction } from 'i18next';

import type { Device } from '@/store/Station/constants';
import type { DeviceDrawerSchema } from '@/comp/StationControlCenter/types/device-schema.types';
import type { RealtimeMetric } from '@/comp/StationControlCenter/types/universal-device-mapper.types';
import type { SliderPoint } from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider.types';

import { getLabelByKey, getSliderPointsFromMetric } from '@/comp/StationControlCenter/UniversalDeviceWidget/UniversalDeviceWidget.utils';
import {
  buildAdditionalContext,
  getDeviceValue,
  getMetricUnit,
} from '@/comp/StationControlCenter/UniversalDataMapper/UniversalDeviceDataMapper';

type MeasurementFns = {
  tvalue: (value: number, unit?: string) => number;
  tunit: (unit?: string) => string;
};

export type MetricItemVM = { label: string; value: string | number; unit?: string };

export type RealtimeDataSectionVM = {
  realtimeMetrics: RealtimeMetric[];
  sliderMetric?: RealtimeMetric;
  metrics: RealtimeMetric[];
  sliderPoints: SliderPoint[];
  hasContent: boolean;
  sliderTitle: string;
  metricItems: MetricItemVM[];
};

type DeviceWithExtendedProperties = Device & Record<string, unknown>;

export const buildRealtimeDataSectionVM = ({
  device,
  schema,
  t,
  measurement,
}: {
  device: Device;
  schema?: DeviceDrawerSchema;
  t: TFunction;
  measurement: MeasurementFns;
}): RealtimeDataSectionVM => {
  const { tvalue, tunit } = measurement;
  const deviceExtended = device as DeviceWithExtendedProperties;

  const realtimeMetrics: RealtimeMetric[] = (schema?.realtimeMetrics ?? []).reduce<RealtimeMetric[]>(
    (acc, metric) => {
      const value = getDeviceValue(deviceExtended, metric.key);
      if (value === undefined || (typeof value !== 'number' && typeof value !== 'string')) {
        return acc;
      }

      acc.push({
        key: metric.key,
        value,
        unit: getMetricUnit(metric.key),
        type: metric.type,
        additionalContext: buildAdditionalContext(deviceExtended, metric.additionalContext),
      });
      return acc;
    },
    [],
  );

  const sliderMetric = realtimeMetrics.find(metric => metric.type === 'slider');
  const metrics = realtimeMetrics.filter(metric => metric.type !== 'slider');
  const sliderPoints = getSliderPointsFromMetric({ metric: sliderMetric, t, tvalue });

  const hasContent = sliderMetric !== undefined || metrics.length > 0;

  const sliderTitle =
    sliderMetric?.type === 'slider' && sliderMetric.additionalContext?.target === undefined
      ? t(getLabelByKey(sliderMetric.key))
      : '';

  const metricItems = metrics.map(metric => {
    const label = t(getLabelByKey(metric.key));
    const hasValue = metric.value !== null && metric.value !== undefined;
    if (typeof metric.value === 'string' && metric.value !== 'N/A') {
      return { label, value: t(metric.value) };
    }
    const rawValue = hasValue ? tvalue(Number(metric.value), metric.unit) : undefined;
    const value = rawValue !== undefined ? rawValue : t('N/A');
    const unit = rawValue !== undefined && metric.unit ? t(tunit(metric.unit)) : undefined;

    return { label, value, unit };
  });

  return {
    realtimeMetrics,
    sliderMetric,
    metrics,
    sliderPoints,
    hasContent,
    sliderTitle,
    metricItems,
  };
};
